import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePersonas } from '@/hooks/usePersonas'
import type { ApiPersona } from '@/types/persona'

interface BasicsValues { name: string; description: string; tags: string[]; isPublic: boolean }

export interface PersonaBasicsDraft {
  values: BasicsValues
  setField: <K extends keyof BasicsValues>(k: K, v: BasicsValues[K]) => void
  isDirty: boolean
  save: () => Promise<void>
  /** Drop unsaved edits: restore values to the last saved baseline. */
  reset: () => void
}

function seed(p: ApiPersona): BasicsValues {
  return {
    name: p.name ?? '',
    description: p.description ?? '',
    tags: p.tags ?? [],
    isPublic: p.is_public ?? false,
  }
}

function valuesEqual(a: BasicsValues, b: BasicsValues): boolean {
  return (
    a.name === b.name &&
    a.description === b.description &&
    JSON.stringify(a.tags) === JSON.stringify(b.tags) &&
    a.isPublic === b.isPublic
  )
}

/** Local draft for persona basics; flushed by the top-bar Save via the
 *  partial-safe updatePersona (spec §4.5.1, §10.1). */
export function usePersonaBasicsDraft(persona: ApiPersona): PersonaBasicsDraft {
  const { updatePersona } = usePersonas()
  const initial = useMemo(() => seed(persona), [persona])
  const [values, setValues] = useState<BasicsValues>(initial)
  const [baseline, setBaseline] = useState<BasicsValues>(initial)

  // Re-seed both when a different persona is passed (keyed on id; save never
  // changes the id, so this won't re-fire on save).
  useEffect(() => {
    const s = seed(persona)
    setValues(s)
    setBaseline(s)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona.id])

  const isDirty = !valuesEqual(values, baseline)

  const setField = useCallback<PersonaBasicsDraft['setField']>((k, v) => {
    setValues((prev) => ({ ...prev, [k]: v }))
  }, [])

  const save = useCallback(async () => {
    const snapshot = values
    if (valuesEqual(snapshot, baseline)) return
    await updatePersona(persona.id, {
      name: snapshot.name,
      description: snapshot.description,
      tags: snapshot.tags,
      ...(snapshot.isPublic !== baseline.isPublic ? { isPublic: snapshot.isPublic } : {}),
    })
    setBaseline(snapshot)
  }, [persona.id, updatePersona, values, baseline])

  const reset = useCallback(() => {
    setValues(baseline)
  }, [baseline])

  return { values, setField, isDirty, save, reset }
}
